import {
  Entity,
  formatTicker,
  isMappedEntity,
  MappedEntity,
  SnakeMeta,
} from '@api';
import { RangeHorizon, rangeToResampleMapping } from '@chart';
import { Badge, EntityDetails } from '@design';
import {
  type DropdownMenuContentProps,
  Content as DropdownMenuContent,
} from '@radix-ui/react-dropdown-menu';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { useChartNavigate } from '@terminal/hooks/use-chart-navigate/useChartNavigate';
import { ChartPrice } from '@terminal/modules/simple-chart/components/chart-price/ChartPrice';
import { SimpleChart } from '@terminal/modules/simple-chart/SimpleChart';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { ChartSearchParamsAssetValue } from '@terminal/utils/chart-search-utils/chart-search-utils';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import React, { forwardRef, MouseEvent, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ChartPreview.module.scss';

export interface ChartPreviewProps extends DropdownMenuContentProps {
  entity: Entity | MappedEntity;
  horizon: RangeHorizon;
  onMouseLeave: MouseEventHandler<HTMLDivElement>;
  onMouseEnter: MouseEventHandler<HTMLDivElement>;
  enableHover?: boolean;
  snakeMeta?: SnakeMeta;
  source: PosthogTrackingSource;
}

export const ChartPreview = forwardRef<HTMLDivElement, ChartPreviewProps>(
  (
    {
      entity,
      horizon,
      onMouseLeave,
      onMouseEnter,
      enableHover = true,
      snakeMeta,
      source,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation(['chart', 'analyze']);
    const chartNavigate = useChartNavigate();
    const formattedTicker = formatTicker(entity);
    const entityName = entity.name_short || entity.name_full;
    const showLivePrice = !!snakeMeta && !isMappedEntity(entity);

    const onClick = (e: MouseEvent) => {
      e.stopPropagation();

      if ((e.target as HTMLElement).tagName !== 'CANVAS') {
        return;
      }

      const entityValue: ChartSearchParamsAssetValue = [
        entity.tag,
        entity.default_snake,
        0,
      ];
      chartNavigate([entityValue], {
        resample: rangeToResampleMapping[horizon],
        horizon,
      });
    };

    const initialChartSettings: ChartSettings = {
      assets: [
        {
          tag: entity.tag,
          snake: entity.default_snake,
          paneIndex: 0,
        },
      ],
      resample: rangeToResampleMapping[horizon],
      horizon,
      domain: null,
      hidden: undefined,
      paneSettings: {},
    };

    return (
      <DropdownMenuContent
        className={styles.content}
        onClick={onClick}
        side="right"
        align="start"
        sideOffset={-10}
        onMouseLeave={onMouseLeave}
        onMouseEnter={onMouseEnter}
        data-testid="chart-preview"
        ref={ref}
        {...rest}
      >
        <EntityDetails
          className={styles.styledEntityDetails}
          entityNameComponent={
            <TruncateTooltip
              placement="top"
              atLength={20}
              text={entityName}
              data-testid="analyze-header-title"
            />
          }
          formattedTicker={formattedTicker}
          leftImgNode={<AssetLogoContainer entity={entity} />}
        />
        {showLivePrice && (
          <ChartPrice
            entity={entity}
            snakeMeta={snakeMeta}
            enablePriceHover={enableHover}
          />
        )}
        <div className={styles.chartWrapper}>
          <div className={styles.horizonLabel}>
            <Badge
              color="neutral"
              size="small"
              badgeStyle="outline"
              label={
                /** i18next-extract-mark-context-next-line ["OneMonth"] */
                t('chart:intervals.horizon', { context: horizon })
              }
            />
          </div>
          <SimpleChart
            initialChartSettings={initialChartSettings}
            shouldShowChartHeader={false}
            chartConfig={{ enableHover }}
            source={source}
          />
        </div>
      </DropdownMenuContent>
    );
  }
);
